'use client';
import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import Image from '@repo/ui/src/components/Image';
import Section from '@repo/ui/src/components/Section';
import { useReducedMotion } from '@repo/ui/src/utils/useMediaQuery';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { imageFullWidthSmAndUp } from '@style/variables.js';
import { MediaType } from '@repo/ui/src/types/image';
import { ToutContentProps } from '@repo/ui/src/types/toutContent';
import { ToutContent as BaseToutContent } from '@repo/ui/src/components/ToutContent';
import { MetaProps } from '@repo/ui/src/types';

interface PelletImageProps {
  endpoint: string;
  name: string;
}

export interface Props {
  toutContent: ToutContentProps;
  heroImage: {
    image: MediaType;
    static: boolean;
    _meta: MetaProps;
  };
}

const FuelHero = ({ toutContent, heroImage }: Props) => {
  const ref = useRef<HTMLElement>(null);
  const pelletRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  const heroImageUrl = buildImageSrc(heroImage, imageFullWidthSmAndUp, 10, 4);
  const pelletImage: PelletImageProps = {
    endpoint: 'traeger',
    name: 'pellets-warm',
  };
  const pelletImageUrl = buildImageSrc(pelletImage, 'auto', 0, 0);
  const HeaderEl = toutContent?.headerVariation?.headerVariation || 'h2';

  useEffect(() => {
    if (reduceMotion) return;

    const element = ref.current;
    const pelletElement = pelletRef.current;

    if (element && pelletElement) {
      gsap.set(pelletElement, {
        yPercent: 0,
      });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: 'top bottom',
          end: 'bottom 25%',
          scrub: 1,
          immediateRender: true,
          invalidateOnRefresh: true,
        },
      });

      timeline.fromTo(pelletElement, { yPercent: 0 }, { yPercent: -35 });

      return () => {
        timeline.kill();
      };
    }
  }, [reduceMotion]);

  return (
    <Section
      as="section"
      texture
      rootRef={ref}
      className="pb-12 sm:pt-20 sm:pb-15 md:pt-[30vh] md:pb-20"
      zIndexNone
    >
      <div
        data-testid="fuel-hero"
        ref={pelletRef}
        className="h-full mix-blend-multiply w-3/4 absolute left-1/2 top-0 opacity-70 transform translate-x-[-50%] translate-y-0 sm:w-3/5 md:w-1/2 lg:w-[43%] max-w-[1080px]"
      >
        <Image
          src={pelletImageUrl}
          layout="fill"
          alt=""
          role="presentation"
          className="object-cover"
        />
      </div>
      <div
        className="relative flex flex-col justify-center items-center z-1 bg-cover bg-no-repeat bg-center py-20 px-5 mb-12 sm:mb-20 sm:py-36"
        style={{ backgroundImage: `url("${heroImageUrl}")` }}
      >
        {(toutContent.headingLight || toutContent.heading) && (
          <HeaderEl className="flexible text-warm-100 text-center tra-text-web-display-1">
            {toutContent.headingLight && (
              <>
                <span className="block font-display46">{toutContent.headingLight}</span>
                {/* Add spaces for SEO */}
                <span className="sr-only">&nbsp;</span>
              </>
            )}
            <span className="">{toutContent.heading}</span>
          </HeaderEl>
        )}
      </div>
      <BaseToutContent
        align="center"
        fullWidth
        description={toutContent.description}
        buttons={toutContent.buttons}
        className="px-10 md:px-gutterLG"
      />
    </Section>
  );
};

export default FuelHero;
